/**
 * Slice 47 — cleaner leave-out spots, per party size.
 * Extras (where guests find spares) live on the inventory item, not in this plan.
 */

export type LeaveOutSpot = {
  id: string;
  qty: number;
  /** Phrase as typed: "on the queen bed", "under the kitchen sink". */
  place: string;
  /** The owner's spot (photo group) this place is, so the cleaner sees its photo. Optional. */
  group_id?: string | null;
};

export type LeaveOutPartyRow = {
  id: string;
  /** null = every stay */
  guests: number | null;
  spots: LeaveOutSpot[];
};

export type LeaveOutPlan = {
  rules: LeaveOutPartyRow[];
};

export type ResolvedLeaveOut = {
  leaveQty: number;
  where: string;
  /** Each place with its amount (and spot, when known). */
  spots: { qty: number; place: string; groupId: string | null }[];
  photos: { name: string; photo_url: string }[];
  usedSmallest: boolean;
};

function newId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function isRecord(x: unknown): x is Record<string, unknown> {
  return x !== null && typeof x === "object" && !Array.isArray(x);
}

export function emptyLeaveOutPlan(): LeaveOutPlan {
  return { rules: [] };
}

function parseSpot(raw: unknown): LeaveOutSpot | null {
  if (!isRecord(raw)) return null;
  const place = typeof raw.place === "string" ? raw.place : "";
  const qty =
    typeof raw.qty === "number" && Number.isFinite(raw.qty) && raw.qty >= 0 ? Math.floor(raw.qty) : 0;
  return {
    id: typeof raw.id === "string" && raw.id.trim() ? raw.id.trim() : newId(),
    qty,
    place,
    group_id: typeof raw.group_id === "string" && raw.group_id.trim() ? raw.group_id.trim() : null,
  };
}

/** Older plans stored one shared place list. Turn those into spots on each rule. */
function spotsFromLegacy(rule: Record<string, unknown>, places: { id: string; name: string }[]): LeaveOutSpot[] {
  const qty = isRecord(rule.qty) ? rule.qty : {};
  const spots: LeaveOutSpot[] = [];
  for (const place of places) {
    const n = qty[place.id];
    if (typeof n === "number" && n > 0) {
      spots.push({ id: newId(), qty: Math.floor(n), place: place.name });
    }
  }
  return spots;
}

export function parseLeaveOutPlan(raw: unknown): LeaveOutPlan | null {
  if (!isRecord(raw) || !Array.isArray(raw.rules)) return null;
  const legacyPlaces: { id: string; name: string }[] = [];
  if (Array.isArray(raw.places)) {
    for (const p of raw.places) {
      if (!isRecord(p)) continue;
      const name = typeof p.name === "string" ? p.name.trim() : "";
      const id = typeof p.id === "string" ? p.id : "";
      if (name && id) legacyPlaces.push({ id, name });
    }
  }
  const rules: LeaveOutPartyRow[] = [];
  for (const r of raw.rules) {
    if (!isRecord(r)) continue;
    const guests =
      typeof r.guests === "number" && Number.isFinite(r.guests) && r.guests >= 1
        ? Math.floor(r.guests)
        : null;
    let spots: LeaveOutSpot[] = [];
    if (Array.isArray(r.spots)) {
      spots = r.spots.map(parseSpot).filter((s): s is LeaveOutSpot => s != null);
    } else {
      spots = spotsFromLegacy(r, legacyPlaces);
    }
    rules.push({
      id: typeof r.id === "string" && r.id.trim() ? r.id.trim() : newId(),
      guests,
      spots,
    });
  }
  if (rules.length === 0) return null;
  return { rules };
}

function filledSpots(rule: LeaveOutPartyRow): LeaveOutSpot[] {
  return rule.spots.filter((s) => s.qty > 0 && s.place.trim());
}

export function planHasLeaveOut(plan: LeaveOutPlan | null | undefined): boolean {
  if (!plan) return false;
  return plan.rules.some((rule) => filledSpots(rule).length > 0);
}

export function leaveOutRuleSentence(rule: LeaveOutPartyRow): string {
  const parts = filledSpots(rule);
  const total = parts.reduce((sum, p) => sum + p.qty, 0);
  const head = rule.guests == null ? "Every stay" : `If ${rule.guests} guests`;
  if (total <= 0) return `${head}, leave nothing`;
  return `${head}, leave ${total} — ${parts.map((p) => `${p.qty} ${p.place.trim()}`).join(", ")}`;
}

export function leaveOutListLines(plan: LeaveOutPlan): string[] {
  const party = plan.rules
    .filter((r) => r.guests != null)
    .slice()
    .sort((a, b) => (a.guests ?? 0) - (b.guests ?? 0));
  const every = plan.rules.find((r) => r.guests == null);
  const source = party.length > 0 ? party : every ? [every] : [];
  return source.slice(0, 2).map((rule) => {
    const parts = filledSpots(rule);
    const head = rule.guests == null ? "Every stay" : `${rule.guests} guests`;
    if (parts.length === 0) return `${head} → leave nothing`;
    return `${head} → ${parts.map((p) => `${p.qty} ${p.place.trim()}`).join(" · ")}`;
  });
}

export function resolveLeaveOut(
  plan: LeaveOutPlan,
  guestCount: number | null | undefined,
): ResolvedLeaveOut | null {
  const party = plan.rules
    .filter((r) => r.guests != null)
    .slice()
    .sort((a, b) => (a.guests ?? 0) - (b.guests ?? 0));
  const every = plan.rules.find((r) => r.guests == null) ?? null;
  const known =
    typeof guestCount === "number" && Number.isFinite(guestCount) && guestCount >= 1
      ? Math.floor(guestCount)
      : null;

  let rule: LeaveOutPartyRow | null = null;
  let usedSmallest = false;
  if (known != null) {
    rule = party.find((r) => r.guests === known) ?? every;
  } else if (every) {
    rule = every;
  } else if (party[0]) {
    rule = party[0];
    usedSmallest = true;
  }
  if (!rule) return null;

  const parts = filledSpots(rule);
  const leaveQty = parts.reduce((sum, p) => sum + p.qty, 0);
  if (leaveQty <= 0) return null;
  return {
    leaveQty,
    where: parts.map((p) => `${p.qty} ${p.place.trim()}`).join(" · "),
    spots: parts.map((p) => ({ qty: p.qty, place: p.place.trim(), groupId: p.group_id ?? null })),
    photos: [],
    usedSmallest,
  };
}

export function nextGuestCount(used: number[], from: number, dir: 1 | -1): number {
  let n = from + dir;
  while (n >= 1 && n <= 16 && used.includes(n)) n += dir;
  if (n < 1 || n > 16) return from;
  return n;
}

export function firstFreeGuestCount(used: number[]): number {
  let n = 2;
  while (used.includes(n) && n < 16) n += 1;
  return n;
}

export function totalForRule(rule: LeaveOutPartyRow): number {
  return filledSpots(rule).reduce((sum, s) => sum + s.qty, 0);
}
